import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { goalsTrackingRequests } from 'redux/requests/goals-tracking.requests';
import { GoalsTrackingState } from 'redux/types/goalsTracking.types';
import { createApiThunk } from 'redux/utils/apiThunk';
import { addToEffectsMiddleware } from 'redux/utils/effectsMiddleware.util';
import { selectMember } from './selectedMemberSlice.slice';
import { GoalsTrackingViewDto } from '@act/shared/data-transfer-objects';
import { useAppSelector } from 'redux/storeHooks';
import { sortAggregatedGoals } from '@act/storybook';
import type { WebsocketEventActions } from '../types/websocketTypes';
import {
  GoalsTrackingWSEvents,
  GoalsUpdatedWSPayload,
} from '@act/shared/socket-events';
import { timeFormatter } from '@act/shared/utilities';
import { DateTime } from 'luxon';

// THUNKS/ACTIONS ==============================================================
export const getGoalsForMember = createApiThunk(
  goalsTrackingRequests.getGoalsForMember
);

export const getGoalsForGuide = createApiThunk(
  goalsTrackingRequests.getGoalsForGuide
);

// INITIAL STATE ===============================================================
const initialState: GoalsTrackingState = {
  status: 'initial',
  goalsByMemberId: {},
};

// SLICE =======================================================================
export const goalsTrackingSlice = createSlice({
  name: 'goalsTracking',
  initialState,
  reducers: {
    updateGoals: (
      state: GoalsTrackingState,
      action: PayloadAction<GoalsUpdatedWSPayload>
    ) => {
      const { memberId, assignedSurveys, consents, needs } =
        action.payload.goalsTrackingView;
      state.goalsByMemberId = {
        ...state.goalsByMemberId,
        [memberId]: {
          assignedSurveys,
          consents,
          needs,
        },
      };
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getGoalsForMember.pending, state => {
        state.status = 'loading';
      })
      .addCase(getGoalsForMember.rejected, state => {
        state.status = 'failed';
      })
      .addCase(
        getGoalsForMember.fulfilled,
        (state, action: PayloadAction<GoalsTrackingViewDto>) => {
          const { memberId, assignedSurveys, consents, needs } = action.payload;
          state.status = 'success';
          state.goalsByMemberId = {
            ...state.goalsByMemberId,
            [memberId]: {
              assignedSurveys,
              consents,
              needs,
            },
          };
        }
      )
      .addCase(getGoalsForGuide.pending, state => {
        state.status = 'loading';
      })
      .addCase(getGoalsForGuide.rejected, state => {
        state.status = 'failed';
      })
      .addCase(
        getGoalsForGuide.fulfilled,
        (state, action: PayloadAction<GoalsTrackingViewDto[]>) => {
          state.status = 'success';
          action.payload.forEach(goal => {
            state.goalsByMemberId = {
              ...state.goalsByMemberId,
              [goal.memberId]: {
                assignedSurveys: goal.assignedSurveys,
                consents: goal.consents,
                needs: goal.needs,
              },
            };
          });
        }
      );
  },
});

// REDUCER =====================================================================
export const goalsTrackingReducer = goalsTrackingSlice.reducer;
export const goalsTrackingActions = goalsTrackingSlice.actions;

// SELECTORS ===================================================================
const selectGoalsTrackingState = state =>
  (state as { goalsTracking: typeof initialState })?.goalsTracking;

const selectGoalsTrackingStatus = state =>
  (state as { goalsTracking: typeof initialState })?.goalsTracking?.status;

const selectGoalsByMemberIdMap = state =>
  (state as { goalsTracking: typeof initialState })?.goalsTracking
    ?.goalsByMemberId;

const selectAggregatedGoalsForMember = (memberId: string) =>
  createSelector(selectGoalsByMemberIdMap, goalsByMemberIdMap => {
    if (!goalsByMemberIdMap[memberId]) {
      return { inProgress: [], completed: [] };
    }

    const { assignedSurveys, consents, needs } = goalsByMemberIdMap[memberId];
    const aggegatedGoals = [...assignedSurveys, ...consents, ...needs];

    return {
      inProgress: sortAggregatedGoals(
        aggegatedGoals.filter(goal => !goal.completed)
      ),
      completed: sortAggregatedGoals(
        aggegatedGoals.filter(goal => goal.completed),
        'DESC'
      ),
    };
  });

const selectAggregatedPendingGoalsForGuide = (showOnlyDueToday?: boolean) =>
  createSelector(selectGoalsByMemberIdMap, goalsByMemberIdMap => {
    return Object.values(goalsByMemberIdMap).reduce((acc, goals) => {
      let aggregatedPendingGoals = [
        ...goals.assignedSurveys,
        ...goals.needs,
      ].filter(goal => !goal.completed);

      if (showOnlyDueToday) {
        aggregatedPendingGoals = aggregatedPendingGoals.filter(goal => {
          if (goal.goalType === 'need') {
            if (!goal.dueDate) return false;

            const differenceFromToday = timeFormatter.differenceInDaysFromNow(
              goal.dueDate
            );

            return differenceFromToday === 0;
          }

          if (goal.goalType === 'survey') {
            if (!goal.dueAt) return false;

            const differenceFromToday = timeFormatter.differenceInDaysFromNow(
              DateTime.fromISO(goal.dueAt).toMillis()
            );

            return differenceFromToday === 0;
          }

          return false;
        });
      }

      return [...acc, ...aggregatedPendingGoals];
    }, []);
  });

// EXPORT HELPER ===============================================================
export const goalsTrackingSelectors = {
  useSelectGoalsTrackingState: () => useAppSelector(selectGoalsTrackingState),
  useSelectGoalsTrackingStatus: () => useAppSelector(selectGoalsTrackingStatus),
  useSelectGoalsByMemberIdMap: () => useAppSelector(selectGoalsByMemberIdMap),
  useSelectAggregatedGoalsForMember: (memberId: string) =>
    useAppSelector(selectAggregatedGoalsForMember(memberId)),
  useSelectAggregatedPendingGoalsForGuide: (showOnlyDueToday?: boolean) =>
    useAppSelector(selectAggregatedPendingGoalsForGuide(showOnlyDueToday)),
};

// EFFECTS =====================================================================
addToEffectsMiddleware(addEffect => {
  addEffect({
    action: selectMember,
    effect: async ({ dispatch, action }) => {
      if (action.payload) {
        void dispatch(
          getGoalsForMember({ params: { memberId: action.payload } })
        );
      }
    },
  });
  // addEffect({
  //   action: verifyUser.fulfilled,
  //   effect: async ({ dispatch, action }) => {
  //     dispatch(
  //       getGoalsForGuide({ params: { guideId: action.payload.user.id } })
  //     );
  //   },
  // });
});

// WEBSOCKET ACTIONS ===========================================================
export const goalsTrackingWebsocketActions: WebsocketEventActions[] = [
  {
    type: GoalsTrackingWSEvents.GOALS_UPDATED,
    action: goalsTrackingSlice.actions.updateGoals,
    channel: 'baseSocket',
  },
];
